/**
 * THE CHOREOGRAPHER (B4): every map sequence, spec §D.4, §D.10, §D.11 and §E.
 *
 *   const choreo = createChoreographer(ctrl, { getState: getApp });   // after ctrl.ready
 *   choreo.select(id) · clear() · bloom(cat) · clearBloom() · preview(id) · drive(id) · intro() · home()
 *
 * On creation it pushes every route (kind "route", id = place id) and the site outline (kind "site",
 * closed, starting at the vertex nearest the route origin) into the LightLayer, hands the places to the
 * markers, and lights the site at rest (§D.4).
 *
 * Time. Every moment is scheduled from the sequence's t0 on the shared ticker (delayed tweens) or with
 * ticker.wait, so each value is a pure function of `now`. Nothing goes through React.
 *
 * Ownership of values. Path state goes through PathFx channels (a new tween on head/tail/alpha/pulse
 * cancels the old one), and each sequence claims the paths it drives, so an exit that finishes late never
 * resets a path another sequence has taken over. Each sequence has its own AbortController; destroy()
 * aborts them all and leaves no ticker subscriber, timer or listener behind.
 *
 * Interruption (§E.1). User input stops only the camera (the controller resolves "interrupted"); lines,
 * events, veil and lit contours finish on schedule. The drive is the exception: any input ends it.
 *
 * Bus events emitted (exact timings in the handoff notes of B4):
 *   route:start {placeId, durationMs}   at select t = 0; durationMs = Tarr (reduced: 240). The card docks at
 *                                        route:start + durationMs − 120.
 *   route:progress {p, km, minutes}      eased p = head/L while the head draws (≤ 30 Hz), then p = 1.
 *   route:drawn                          at Tarr (reduced: 240).
 *   route:cleared                        when a retract finishes.
 *   bloom:start / bloom:arrive / bloom:done, drive:progress / drive:end, intro:beat, camera:interrupt {cancel}.
 */
import type { Category, CategoryKind, LngLat, Place, RouteRecord } from "@/contracts/data";
import { displayTravel } from "@/contracts/data";
import type { CameraTarget, Choreographer, EaseName, LightLayerAPI, MapController, MarkersAPI, MoveResult, OverlaysAPI } from "@/contracts/map";
import type { AppState, Insets, Mood, SelectSource } from "@/contracts/store";
import type { IntroBeat } from "@/contracts/bus";
import { REGION_BBOX } from "@/contracts/taxonomy";
import { EASE } from "@/design";
import { haversine } from "@/lib/geo";
import { isAbort, ticker, tween, wait } from "@/lib/ticker";
import { HOME_RING_M, safeInsets, zoomForRadius, type ControllerHandle } from "@/map/controller";
import { MOOD_SUNRISE_MS } from "@/map/style/moods";
import { bus, on } from "@/store/bus";
import { actions } from "@/store/store";
import { InputTracker, runIdleLoop } from "../anim/idle";
import { PathFx } from "../anim/pathFx";
import {
  AB_RETRACT,
  DRIVE_ARRIVE_HOLD_MS,
  DRIVE_ENTER_MS,
  DRIVE_PITCH,
  DRIVE_START_BEARING_M,
  EXIT,
  FIRST_PACKET_AFTER_MS,
  INTRO,
  INTRO_BEATS,
  LIT,
  REDUCED,
  RINGS,
  bloomExitPlan,
  bloomPacketIntervalMs,
  bloomPlan,
  cameraMs,
  driveMs,
  driveP,
  driveTimeAt,
  driveZoom,
  litRadiusPx,
  lookAheadM,
  packetIntervalMs,
  packetMs,
  packetSigmaM,
  ringRadiusPx,
  selectTimeline,
  type BloomPlan,
  type SelectTimeline,
} from "../anim/timing";
import { captionAt } from "../route/captions";
import { SITE_PATH_ID, corridorCoords, lodCoords, routeFor, routePaths, sitePath, thin } from "../route/paths";
import { fitInBounds } from "./fit";
import { bloomFraming, chooseMove, driveFramePadding, framePadding, selectFraming, selectPitchRange, signedBearing } from "./framing";
import { chordBearing, lookAt, stepBearing, wrap360, type RouteGeom, type Spring } from "./followCam";

export interface ChoreoDeps {
  /** Latest store snapshot (data, routes, geo, insets, layout, reducedMotion…). */
  getState: () => AppState;
  /** Random source for the idle packet intervals (default Math.random). */
  random?: () => number;
}

/** Non-contract extras for harnesses and QA. */
export interface ChoreoDebug {
  selected: string | null;
  arrived: boolean;
  bloom: string | null;
  bloomArrived: number;
  driving: string | null;
  intro: boolean;
  preview: string | null;
  siteLit: boolean;
  lastTarget: CameraTarget | null;
  lastTiming: SelectTimeline | null;
  idleLoops: number;
}
export type ChoreographerHandle = Choreographer & { debug(): ChoreoDebug; framing(id: string): CameraTarget | null };

/**
 * B5's OverlaysAPI extensions (src/map/layers OverlaysHandle), used structurally: extra fields are ignored by
 * a plain contract implementation, so the choreography degrades gracefully without them.
 *   setRoads.bloom      corridors lit at bloom strength as their routes arrive (§E.4 arterial roads)
 *   setFuture.bloom     every planned corridor at full strength with its label (§E.4 future infrastructure)
 *   lightContours.delayMs / ease   build now (hidden), rise later (§D.10: built at select t = 0, rise at Tarr)
 */
type OverlaysExt = Omit<OverlaysAPI, "setRoads" | "setFuture" | "lightContours"> & {
  setRoads(o: { visible: boolean; highlight?: string | null; bloom?: string[] }): void;
  setFuture(o: { visible: boolean; highlight?: string | null; bloom?: boolean }): void;
  lightContours(at: LngLat | null, o?: { zoom: number; radiusPx?: number; rest?: number; durationMs?: number; delayMs?: number; ease?: EaseName }): void;
};

/** Room at the frame's edge (CSS px): the line and its glow, the destination pin and its rings, the Whorl and its label. */
const MARGIN = { line: 12, pin: 36, pinKiosk: 44, site: 30, bloomPin: 24 } as const;
/** The select camera never goes closer than this (short routes keep the site's neighbourhood). */
const SELECT_MAX_ZOOM = 15;
const SELECT_MIN_ZOOM = 8;
/** Other drawn routes while something is selected (§E.1). */
const CONTEXT_ALPHA = 0.25;
const BLOOM_VEIL = 0.6;
const PREVIEW_ALPHA = 0.5;
const PROGRESS_HZ_MS = 1000 / 30;
const DRIVE_PACKET_SIGMA_M = 300;
/** Resume a stopped drive from where it stopped if Resume comes within this long. */
const RESUME_WINDOW_MS = 120_000;
/** An interrupted drive: bearing and pitch ease back to the select pose (§E.6 Stop, §E.11). */
const TAKEOVER_SETTLE_MS = 640;

const noopMarkers: MarkersAPI = {
  setPlaces: () => {},
  setFocusSet: () => {},
  setSelected: () => {},
  setHovered: () => {},
  setDimmed: () => {},
  arrive: () => {},
  pulse: () => {},
  setSite: () => {},
  setMood: () => {},
  destroy: () => {},
};
const noopOverlays: OverlaysExt = {
  setBoundary: () => {},
  setRings: async () => {},
  setRoads: () => {},
  setFuture: () => {},
  setVeil: () => {},
  lightContours: () => {},
  setMood: () => {},
  destroy: () => {},
};

interface Sel {
  id: string;
  place: Place;
  kind: CategoryKind;
  route: RouteRecord | null; // null: no line (a future corridor, or no route)
  L: number;
  tl: SelectTimeline;
  t0: number;
  ac: AbortController;
  token: symbol | null;
  target: CameraTarget | null;
  arriveZoom: number;
  arrived: boolean;
  lastP: number;
  /** When route:progress was last emitted (≤ 30 Hz). */
  pt: number;
  corridor: { kind: "road" | "future"; id: string } | null;
  resolve: () => void;
  done: Promise<void>;
}

interface BloomRun {
  cat: string;
  kind: CategoryKind;
  plan: BloomPlan;
  ac: AbortController;
  tokens: Map<string, symbol>;
  arrived: Set<string>;
  corridors: string[];
  count: number;
  finished: boolean;
  target: CameraTarget | null;
  /** Counts a route as arrived (also when a selection takes it over mid-draw). */
  arriveId: (id: string) => void;
  resolve: () => void;
  done: Promise<void>;
}

interface DriveRun {
  id: string;
  ac: AbortController;
  stop: (reason: "interrupted" | "cancelled" | "internal") => void;
  p: number;
}

interface IntroRun {
  ac: AbortController;
  done: Promise<void>;
  finish: () => void;
}

const deferred = (): { promise: Promise<void>; resolve: () => void } => {
  let resolve!: () => void;
  const promise = new Promise<void>((r) => (resolve = r));
  return { promise, resolve };
};

/** Resolves true after ms, false when the signal aborts first. Never rejects. */
async function sleep(ms: number, signal?: AbortSignal): Promise<boolean> {
  try {
    await wait(Math.max(0, ms), signal);
    return true;
  } catch (e) {
    if (!isAbort(e)) console.error("[choreo]", e);
    return false;
  }
}

export function createChoreographer(ctrl: MapController, deps: ChoreoDeps): ChoreographerHandle {
  const handle = ctrl as Partial<ControllerHandle> & MapController;
  const S = () => deps.getState();
  const rand = deps.random ?? Math.random;
  let destroyed = false;

  const light = (): LightLayerAPI | null => {
    if (destroyed) return null;
    try {
      return ctrl.light();
    } catch {
      return null;
    }
  };
  const markers = (): MarkersAPI => {
    try {
      return destroyed ? noopMarkers : ctrl.markers();
    } catch {
      return noopMarkers;
    }
  };
  const overlays = (): OverlaysExt => {
    try {
      return destroyed ? noopOverlays : (ctrl.overlays() as unknown as OverlaysExt);
    } catch {
      return noopOverlays;
    }
  };
  const safe = (what: string, f: () => void): void => {
    try {
      f();
    } catch (err) {
      console.warn(`[choreo] ${what}`, err);
    }
  };

  const fx = new PathFx(light);
  const tracker = new InputTracker();
  const reduced = (): boolean => !!S().reducedMotion;
  const kiosk = (): boolean => S().layout === "kiosk" || S().kiosk;
  const viewSize = (): { w: number; h: number } => {
    try {
      const el = ctrl.map().getContainer();
      return { w: el.clientWidth || window.innerWidth, h: el.clientHeight || window.innerHeight };
    } catch {
      return { w: typeof window !== "undefined" ? window.innerWidth : 1440, h: typeof window !== "undefined" ? window.innerHeight : 900 };
    }
  };
  const siteCenter = (): LngLat => {
    try {
      return handle.siteCenter?.() ?? ctrl.homeTarget().center;
    } catch {
      return S().data?.origin.coord ?? [73.960862, 18.605185];
    }
  };
  const placeOf = (id: string): Place | null => S().data?.places.find((p) => p.id === id) ?? null;
  const categoryOf = (p: Place | null): Category | null => (p ? (S().data?.categories.find((c) => c.id === p.category) ?? null) : null);
  const emit = bus.emit.bind(bus);

  /* ── paths and places on creation ────────────────────────────────── */

  const st0 = S();
  const site = sitePath(st0.geo?.site, (st0.routes?.origin ?? st0.data?.origin.coord ?? null) as LngLat | null);
  const siteL = site?.lengthM ?? 0;
  safe("setPaths", () => light()?.setPaths([...routePaths(st0.routes), ...(site ? [site] : [])]));
  safe("setPlaces", () => st0.data && markers().setPlaces(st0.data.places, st0.data.categories));

  /* ── state ───────────────────────────────────────────────────────── */

  let sel: Sel | null = null;
  let bloom: BloomRun | null = null;
  let driving: DriveRun | null = null;
  let intro: IntroRun | null = null;
  let previewId: string | null = null;
  let previewToken: symbol | null = null;
  let siteLit = false;
  let siteAc: AbortController | null = null;
  let lastTarget: CameraTarget | null = null;
  let lastTiming: SelectTimeline | null = null;
  let lastInterrupt: { id: string; p: number; at: number } | null = null;
  let idleLoops = 0;
  const exits = new Set<() => void>(); // finishers of retracts in flight (cancel() snaps them)

  /* ── ambient: veil, site light ───────────────────────────────────── */

  const veilTarget = (): number => (driving || sel ? 1 : bloom ? BLOOM_VEIL : 0);
  const setVeil = (v: number, duration: number, ease: EaseName): void =>
    safe("setVeil", () => overlays().setVeil(v, { duration: reduced() ? REDUCED.fade : duration, ease: reduced() ? "linear" : ease }));
  const siteWanted = (): boolean => !sel && !bloom && !driving && !intro;

  /** Site light at rest (§D.4): on = site style drawn whole, alpha → 1. */
  function setSiteLight(on: boolean, duration: number, ease: EaseName, delay = 0): void {
    if (!site) return;
    siteAc?.abort();
    const ac = (siteAc = new AbortController());
    const r = reduced();
    const dur = r ? REDUCED.fade : duration;
    const go = () => {
      if (ac.signal.aborted || destroyed) return;
      if (on && !siteWanted()) return;
      siteLit = on;
      if (on) {
        const s = fx.get(SITE_PATH_ID);
        if (!s || s.style !== "site" || s.head < siteL - 0.5 || s.tail > 0) fx.set(SITE_PATH_ID, { style: "site", head: siteL, tail: 0, comet: false, pulse: -1, ...(s && s.alpha > 0 && s.head >= siteL - 0.5 ? {} : { alpha: 0 }) });
      }
      void fx.tween(SITE_PATH_ID, "alpha", on ? 1 : 0, dur, r ? EASE.linear : EASE[ease], { signal: ac.signal });
    };
    if (delay > 0 && !r) void sleep(delay, ac.signal).then((ok) => ok && go());
    else go();
  }
  /** Brings the site light back if nothing else is lit (after a retract). */
  const restoreSite = (delay: number): void => {
    if (siteWanted()) setSiteLight(true, 640, "settle", delay);
  };

  // Light at rest from the start (the intro turns it off under the loader and draws it itself).
  if (site) {
    fx.set(SITE_PATH_ID, { style: "site", head: siteL, tail: 0, alpha: 1, comet: false, pulse: -1 });
    siteLit = true;
  }

  /* ── camera helpers ──────────────────────────────────────────────── */

  const restPadding = (): Insets => {
    const { w, h } = viewSize();
    const s = S();
    return driveFramePadding({ insets: s.insets, width: w, height: h, layout: s.layout, kiosk: s.kiosk });
  };
  const cardPadding = (): Insets => {
    const { w, h } = viewSize();
    const s = S();
    return framePadding({ insets: s.insets, width: w, height: h, layout: s.layout, kiosk: s.kiosk, card: true });
  };

  /** homeTarget() with the no-card frame (the card may still be closing when we go home). */
  function homeCamera(): CameraTarget {
    const base = ctrl.homeTarget();
    const { w, h } = viewSize();
    const pad = restPadding();
    const s = safeInsets(pad, w, h);
    const z = zoomForRadius(HOME_RING_M, base.center[1], w - s.left - s.right, h - s.top - s.bottom);
    return { ...base, zoom: Math.round(Math.min(16, Math.max(8, z)) * 1000) / 1000, bearing: 0, pitch: 0, padding: pad };
  }

  function currentCam(): { center: LngLat; zoom: number } {
    const m = ctrl.map();
    const c = m.getCenter();
    return { center: [c.lng, c.lat], zoom: m.getZoom() };
  }

  function moveCamera(target: CameraTarget, duration: number, signal?: AbortSignal): Promise<MoveResult> {
    lastTarget = target;
    let kind: "ease" | "fly" = "ease";
    try {
      const { w, h } = viewSize();
      kind = chooseMove(currentCam(), target, w, h);
    } catch {
      /* map not measurable: ease */
    }
    const o = { duration, ease: "drift" as const, signal };
    return (kind === "fly" ? ctrl.flyTo(target, { ...o, curve: INTRO.curve }) : ctrl.easeTo(target, o)).catch(() => "cancelled" as MoveResult);
  }

  /** §D.11 select framing: the route (or corridor) + pin + site, pitch-aware fit inside the card-open frame. */
  function frameFor(place: Place, route: RouteRecord | null, kind: CategoryKind): { target: CameraTarget; zoom: number; D: number } {
    const { w, h } = viewSize();
    const pad = cardPadding();
    let line: LngLat[];
    let D: number;
    let beta: number;
    if (route) {
      const c = route.coords as LngLat[];
      line = thin(c, Math.ceil(c.length / 400));
      D = route.lengthM / 1000;
      beta = route.framingBearing;
    } else {
      const corridor = kind === "future" || kind === "road" ? corridorCoords(kind === "future" ? S().geo?.future : S().geo?.roads, place.geometryRef) : [];
      const o = S().data?.origin.coord ?? siteCenter();
      line = thin(corridor, Math.ceil(corridor.length / 300));
      D = Math.max(place.crowKm, ...line.map((p) => haversine(o, p) / 1000));
      beta = place.bearing;
    }
    // the site and the destination pin are hard: the route line gives way first (L1-01)
    const pts: LngLat[] = [...line, siteCenter(), place.coord as LngLat];
    const margins = [...line.map(() => MARGIN.line), MARGIN.site, kiosk() ? MARGIN.pinKiosk : MARGIN.pin];
    const { bearing, pitch } = selectFraming(beta, D);
    const fit = fitInBounds(pts, {
      bearing,
      pitch,
      view: { width: w, height: h, padding: pad },
      margins,
      minZoom: SELECT_MIN_ZOOM,
      maxZoom: SELECT_MAX_ZOOM,
      bounds: REGION_BBOX,
      hard: [pts.length - 2, pts.length - 1],
      pitchRange: selectPitchRange(beta, D),
    });
    return { target: { center: fit.center, zoom: fit.zoom, bearing, pitch: fit.pitch, padding: pad }, zoom: fit.zoom, D };
  }

  /** §D.11 bloom framing: every route of the category plus the site, pitch 15, bearing clamped to ±40°. */
  function bloomFrame(points: LngLat[], margins: number[]): CameraTarget {
    const { w, h } = viewSize();
    const pad = restPadding();
    let b = 0;
    try {
      b = ctrl.map().getBearing();
    } catch {
      /* 0 */
    }
    const { bearing, pitch } = bloomFraming(b);
    const ring = site ? site.coords : [siteCenter()];
    const pts = [...points, ...ring];
    const m = [...margins, ...ring.map(() => MARGIN.line)];
    const fit = fitInBounds(pts, { bearing, pitch, view: { width: w, height: h, padding: pad }, margins: m, minZoom: SELECT_MIN_ZOOM, maxZoom: 14, bounds: REGION_BBOX });
    return { center: fit.center, zoom: fit.zoom, bearing, pitch, padding: pad };
  }

  /* ── corridors ───────────────────────────────────────────────────── */

  const roadsQuiet = (): boolean => S().group === "A";
  function corridorOff(c: Sel["corridor"], keep?: Sel["corridor"]): void {
    if (!c || (keep && keep.kind === c.kind && keep.id === c.id)) return;
    if (c.kind === "road") safe("setRoads", () => overlays().setRoads({ visible: roadsQuiet() || !!bloom, highlight: null }));
    else safe("setFuture", () => overlays().setFuture({ visible: S().layers.future || bloom?.kind === "future", highlight: null }));
  }

  /* ── the selected route: arrival and idle packets ────────────────── */

  const displayKm = (p: Place): { km: number; minutes: number } => displayTravel(p) ?? { km: 0, minutes: 0 };

  function progress(s: Sel, p: number, force = false): void {
    const now = performance.now();
    if (!force && now - s.pt < PROGRESS_HZ_MS) return;
    s.pt = now;
    s.lastP = p;
    const t = displayKm(s.place);
    emit("route:progress", { placeId: s.id, p, km: t.km * p, minutes: t.minutes * p });
  }

  function arrive(s: Sel): void {
    if (sel !== s || s.arrived || destroyed) return;
    s.arrived = true;
    const r = reduced();
    if (s.route) {
      fx.set(s.id, { comet: false, head: s.L });
      if (s.lastP < 1) progress(s, 1, true);
      emit("route:drawn", { placeId: s.id });
    }
    const at = s.place.coord as LngLat;
    // the tree rings pass; the lit contours (built at t = 0) rise on their own delay at this moment
    if (!r) safe("pulse", () => markers().pulse(at, { count: RINGS.count, staggerMs: RINGS.staggerMs, radiusPx: ringRadiusPx(kiosk()), durationMs: RINGS.durationMs }));
    if (s.corridor?.kind === "road") {
      const ext = overlays();
      safe("setRoads", () => ext.setRoads({ visible: true, highlight: s.corridor!.id }));
    }
    s.resolve();
    if (s.route && !r) startSelectIdle(s);
  }

  function startSelectIdle(s: Sel): void {
    const L = s.L;
    const Tp = packetMs(L / 1000);
    idleLoops++;
    void runIdleLoop({
      signal: s.ac.signal,
      first: FIRST_PACKET_AFTER_MS,
      interval: () => packetIntervalMs(rand()),
      blocked: () => !!driving || !!intro || S().phase === "drive" || S().phase === "intro" || sel !== s,
      reduced,
      tracker,
      packet: async (signal) => {
        if (sel !== s || !s.token || !fx.owns(s.id, s.token)) return;
        fx.set(s.id, { pulseSigma: packetSigmaM(L) });
        const ok = await fx.tween(s.id, "pulse", L, Tp, EASE.breathe, { from: 0, signal, idle: true });
        if (fx.owns(s.id, s.token)) fx.set(s.id, { pulse: -1 });
        return ok;
      },
    }).finally(() => idleLoops--);
  }

  /** Retract a route (tail → head) and hide it; restores it as a bloom route if its bloom is still on. */
  function retract(id: string, L: number, duration: number, o: { delay?: number; after?: () => void } = {}): Promise<void> {
    const token = fx.claim(id);
    const r = reduced();
    const cur = fx.get(id);
    fx.set(id, { comet: false, pulse: -1 });
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      exits.delete(finish);
      if (!fx.owns(id, token)) return;
      fx.release(id, token);
      if (bloom && bloom.arrived.has(id) && !(sel && sel.id === id)) {
        // back to the bloom it belongs to
        const t = fx.claim(id);
        bloom.tokens.set(id, t);
        fx.set(id, { style: "bloom", head: L, tail: 0, alpha: 0, comet: false, pulse: -1 });
        void fx.tween(id, "alpha", sel ? CONTEXT_ALPHA : 1, r ? REDUCED.fade : 294, r ? EASE.linear : EASE.settle);
      } else fx.set(id, { alpha: 0, head: 0, tail: 0 });
      emit("route:cleared", { placeId: id });
      o.after?.();
    };
    exits.add(finish);
    const p = r
      ? fx.tween(id, "alpha", 0, REDUCED.fade, EASE.linear, { delay: o.delay })
      : fx.tween(id, "tail", Math.max(cur?.head ?? L, cur?.tail ?? 0), duration, EASE.leave, { from: cur?.tail ?? 0, delay: o.delay });
    return p.then((ok) => {
      if (ok) finish();
      else if (!finished && !fx.owns(id, token)) {
        finished = true;
        exits.delete(finish);
      }
    });
  }

  /** Ends a selection's own life (timers, idle, draw) and retracts its line. */
  function leave(s: Sel, mode: "ab" | "clear", next?: Sel | null): Promise<void> {
    s.ac.abort();
    s.resolve();
    corridorOff(s.corridor, next?.corridor ?? null);
    if (!s.route) return Promise.resolve();
    const d = mode === "ab" ? AB_RETRACT * s.tl.Td : EXIT * s.tl.Td;
    return retract(s.id, s.L, d);
  }

  /* ── select (§E.1, §E.3) ─────────────────────────────────────────── */

  function select(id: string, o: { source?: SelectSource; signal?: AbortSignal } = {}): Promise<void> {
    if (destroyed || o.signal?.aborted) return Promise.resolve();
    const place = placeOf(id);
    if (!place) return Promise.resolve();
    if (place.isSite) return home();
    if (sel && sel.id === id) return sel.done;
    if (intro) intro.finish();
    if (driving) driving.stop("internal");
    if (previewId === id) previewToken = null; // the selection takes the path over

    const cat = categoryOf(place);
    const kind: CategoryKind = cat?.kind ?? "poi";
    const route = kind === "future" ? null : routeFor(place, S().routes);
    const r = reduced();
    const { target, zoom, D } = frameFor(place, route, kind);
    const tl = selectTimeline(route ? route.lengthM / 1000 : D);
    lastTiming = tl;
    const ac = new AbortController();
    o.signal?.addEventListener("abort", () => ac.abort(), { once: true });
    const d = deferred();
    const prev = sel;
    const next: Sel = {
      id,
      place,
      kind,
      route,
      L: route?.lengthM ?? 0,
      tl,
      t0: performance.now(),
      ac,
      token: null,
      target,
      arriveZoom: zoom,
      arrived: false,
      lastP: 0,
      pt: -Infinity,
      corridor: place.geometryRef && (kind === "road" || kind === "future") ? { kind: kind === "road" ? "road" : "future", id: place.geometryRef } : null,
      resolve: d.resolve,
      done: d.promise,
    };
    sel = next;

    // A leaves (§E.3): retract 0.5·Td(A); the veil stays.
    if (prev) void leave(prev, "ab", next);

    // The land answers (§D.10): B's lit contours are built now, hidden, and rise when the line arrives
    // (with the tree rings). A's go out first (294 leave; the overlays swap them), so B's delay allows for it.
    const riseAt = r ? 0 : route ? tl.Tarr : tl.Tc;
    const swap = prev?.arrived ? 294 : 0;
    safe("lightContours", () =>
      overlays().lightContours(place.coord as LngLat, {
        zoom: zoom,
        radiusPx: litRadiusPx(kiosk()),
        rest: LIT.rest,
        durationMs: r ? REDUCED.fade : LIT.riseMs,
        delayMs: Math.max(0, riseAt - swap),
      }),
    );

    // t = 0
    safe("markers", () => {
      markers().setSelected(id);
      markers().setDimmed(true);
    });
    if (bloom) {
      for (const [bid, tok] of bloom.tokens) if (bid !== id && fx.owns(bid, tok)) void fx.tween(bid, "alpha", CONTEXT_ALPHA, r ? REDUCED.fade : 420, r ? EASE.linear : EASE.drift);
      if (bloom.tokens.has(id)) bloom.arriveId(id);
    }
    if (siteLit) setSiteLight(false, 294, "leave");
    siteLit = false;
    setVeil(1, tl.Tc, "drift");
    if (next.corridor?.kind === "future") safe("setFuture", () => overlays().setFuture({ visible: true, highlight: next.corridor!.id }));
    void moveCamera(target, tl.Tc, ac.signal);

    // the line
    if (route) {
      const L = route.lengthM;
      next.token = fx.claim(id);
      emit("route:start", { placeId: id, durationMs: r ? REDUCED.line : tl.Tarr });
      if (r) {
        fx.set(id, { style: "hero", head: L, tail: 0, alpha: 0, comet: false, pulse: -1 });
        void fx.tween(id, "alpha", 1, REDUCED.line, EASE.linear, { signal: ac.signal }).then((ok) => ok && arrive(next));
      } else {
        fx.set(id, { style: "hero", head: 0, tail: 0, alpha: 1, comet: false, pulse: -1 });
        void fx
          .tween(id, "head", L, tl.Td, EASE.drift, {
            from: 0,
            delay: tl.drawAt,
            signal: ac.signal,
            also: { comet: true },
            onFrame: (_v, e) => progress(next, e),
          })
          .then((ok) => ok && arrive(next));
      }
    } else {
      // No line (a future corridor): the land answers when the camera has framed it.
      void sleep(r ? REDUCED.camera : tl.Tc, ac.signal).then((ok) => ok && arrive(next));
    }
    return next.done;
  }

  /* ── clear (§E.2) ────────────────────────────────────────────────── */

  async function clear(o: { home?: boolean } = {}): Promise<void> {
    if (destroyed) return;
    if (driving) driving.stop("internal");
    const s = sel;
    if (!s) return;
    sel = null;
    const r = reduced();
    const retracted = leave(s, "clear");
    safe("lightContours", () => overlays().lightContours(null));
    safe("markers", () => {
      markers().setSelected(null);
      markers().setDimmed(!!bloom && bloom.kind !== "future");
    });
    if (bloom) for (const [bid, tok] of bloom.tokens) if (bid !== s.id && fx.owns(bid, tok)) void fx.tween(bid, "alpha", 1, r ? REDUCED.fade : 294, r ? EASE.linear : EASE.leave);
    const camMs = EXIT * s.tl.Tc;
    setVeil(veilTarget(), camMs, "leave");
    const cam = o.home === false ? Promise.resolve<MoveResult>("done") : moveCamera(bloom?.target ?? homeCamera(), camMs);
    // the light comes home once the retract has finished
    void retracted.then(() => restoreSite(0));
    await Promise.all([retracted, cam]);
  }

  /* ── bloom (§E.4) ────────────────────────────────────────────────── */

  function bloomAlpha(): number {
    return sel ? CONTEXT_ALPHA : 1;
  }

  function exitBloom(b: BloomRun, o: { following: boolean }): void {
    b.ac.abort();
    b.resolve();
    if (bloom === b) bloom = null;
    const r = reduced();
    safe("focus", () => markers().setFocusSet(null));
    if (!o.following) safe("dim", () => markers().setDimmed(!!sel));
    // corridors back to rest
    if (b.kind === "road") {
      const hl = sel?.corridor?.kind === "road" && sel.arrived ? sel.corridor.id : null;
      safe("setRoads", () => overlays().setRoads({ visible: roadsQuiet() || !!hl, highlight: hl, bloom: [] }));
    } else if (b.kind === "future") {
      const hl = sel?.corridor?.kind === "future" ? sel.corridor.id : null;
      safe("setFuture", () => overlays().setFuture({ visible: S().layers.future || !!hl, highlight: hl, bloom: false }));
    }
    const plan = bloomExitPlan(b.plan);
    const jobs: Promise<void>[] = [];
    for (const x of plan) {
      const tok = b.tokens.get(x.id);
      if (!tok || !fx.owns(x.id, tok)) continue; // the selection owns it now
      const s = fx.get(x.id);
      if (!s || s.alpha <= 0) {
        fx.set(x.id, { alpha: 0, head: 0, tail: 0, comet: false });
        continue;
      }
      jobs.push(retract(x.id, x.L, r ? REDUCED.fade : x.retract, { delay: r ? 0 : x.start }));
    }
    if (!o.following) setVeil(veilTarget(), EXIT * b.plan.Tcam, "leave");
    void Promise.all(jobs).then(() => restoreSite(0));
  }

  function bloomStart(cat: string): Promise<void> {
    if (destroyed) return Promise.resolve();
    if (bloom?.cat === cat) return bloom.done;
    if (intro) intro.finish();
    if (driving) driving.stop("internal");
    const r = reduced();
    const data = S().data;
    const category = data?.categories.find((c) => c.id === cat) ?? null;
    if (!data || !category) return Promise.resolve();

    // A selection closes when a bloom starts (the bridge deselects in the store too; clear() then no-ops).
    if (sel) {
      const s = sel;
      sel = null;
      void leave(s, "clear");
      safe("lightContours", () => overlays().lightContours(null));
      safe("markers", () => {
        markers().setSelected(null);
        markers().setDimmed(false);
      });
    }
    if (bloom) exitBloom(bloom, { following: true });

    const places = data.places.filter((p) => p.category === cat && !p.isSite);
    const kind = category.kind;
    const ac = new AbortController();
    const d = deferred();
    const routes = S().routes;
    const items =
      kind === "future"
        ? []
        : places
            .map((p) => ({ p, rt: routeFor(p, routes) }))
            .filter((x): x is { p: Place; rt: RouteRecord } => !!x.rt)
            .map((x) => ({ id: x.rt.id, L: x.rt.lengthM, minutes: displayTravel(x.p)?.minutes ?? Number.POSITIVE_INFINITY }));
    let plan = bloomPlan(items);
    const corridors = [...new Set(places.map((p) => p.geometryRef).filter((g): g is string => !!g))];

    // camera
    let pts: LngLat[];
    let margins: number[];
    if (kind === "future") {
      const fut = S().geo?.future;
      const o = data.origin.coord as LngLat;
      const coords = corridors.flatMap((cid) => corridorCoords(fut, cid));
      pts = thin(coords, Math.ceil(coords.length / 600));
      margins = pts.map(() => MARGIN.line);
      const far = coords.reduce((m, c) => Math.max(m, haversine(o, c)), 0) / 1000;
      // the summary counts corridors as they "arrive" with the same stagger
      plan = { ...bloomPlan(corridors.map((cid, i) => ({ id: cid, L: 1000 * (i + 1), minutes: i }))), Tcam: cameraMs(far) };
      plan.slots.forEach((x) => {
        x.start = 0.4 * plan.Tcam + x.index * plan.s;
        x.arrive = x.start;
      });
      plan.doneAt = plan.slots.reduce((m, x) => Math.max(m, x.arrive), 0);
    } else {
      pts = [];
      margins = [];
      for (const x of items) {
        const rt = routes?.routes.find((q) => q.id === x.id);
        if (!rt) continue;
        const c = lodCoords(rt);
        c.forEach((pt, i) => {
          pts.push(pt);
          margins.push(i === c.length - 1 ? MARGIN.bloomPin : MARGIN.line); // each route ends at a pin
        });
      }
    }
    const target = pts.length ? bloomFrame(pts, margins) : null;

    const b: BloomRun = {
      cat,
      kind,
      plan,
      ac,
      tokens: new Map(),
      arrived: new Set(),
      corridors,
      count: places.length,
      finished: false,
      target,
      arriveId: () => {},
      resolve: d.resolve,
      done: d.promise,
    };
    bloom = b;
    emit("bloom:start", { categoryId: cat, count: places.length });
    if (siteLit) setSiteLight(false, 294, "leave");
    siteLit = false;
    setVeil(BLOOM_VEIL, plan.Tcam, "drift");
    if (target) void moveCamera(target, plan.Tcam, ac.signal);
    safe("focus", () => markers().setFocusSet([]));
    // the category leads: the other dots recede (0.5 / 0.25) so they stop competing with its pins (L1-02)
    safe("dim", () => markers().setDimmed(kind !== "future"));

    const finish = () => {
      if (b.finished || bloom !== b) return;
      b.finished = true;
      emit("bloom:done", { categoryId: cat });
      b.resolve();
      if (kind !== "future" && !r) startBloomIdle(b);
    };

    if (kind === "future") {
      // every planned corridor at full strength with its label; no routes, pins or rings (§E.4)
      safe("setFuture", () => overlays().setFuture({ visible: true, highlight: null, bloom: true }));
      plan.slots.forEach((x) => {
        void sleep(r ? 0 : x.start, ac.signal).then((ok) => {
          if (!ok || bloom !== b) return;
          b.arrived.add(x.id);
          emit("bloom:arrive", { categoryId: cat, placeId: places.find((p) => p.geometryRef === x.id)?.id ?? x.id, index: x.index });
          if (b.arrived.size === plan.slots.length) finish();
        });
      });
      if (!plan.slots.length) finish();
      return d.promise;
    }

    if (kind === "road") safe("setRoads", () => overlays().setRoads({ visible: true, highlight: null, bloom: [] }));
    const arriveSlot = (x: BloomPlan["slots"][number]) => {
      if (bloom !== b || b.arrived.has(x.id)) return;
      b.arrived.add(x.id);
      const tok = b.tokens.get(x.id);
      if (tok && fx.owns(x.id, tok)) fx.set(x.id, { comet: false, head: x.L });
      // the pin arrives (the markers add it to the focus set themselves) with one ground ring
      safe("arrive", () => markers().arrive(x.id, { ring: !r }));
      if (kind === "road") {
        // arterial roads: each corridor lights at bloom strength as its route arrives
        const lit = places.filter((p) => b.arrived.has(p.route?.routeId ?? p.id) && p.geometryRef).map((p) => p.geometryRef as string);
        safe("setRoads", () => overlays().setRoads({ visible: true, highlight: null, bloom: lit }));
      }
      emit("bloom:arrive", { categoryId: cat, placeId: x.id, index: x.index });
      if (b.arrived.size === plan.slots.length) finish();
    };
    b.arriveId = (id) => {
      const x = plan.slots.find((q) => q.id === id);
      if (x) arriveSlot(x);
    };

    for (const x of plan.slots) {
      const tok = fx.claim(x.id);
      b.tokens.set(x.id, tok);
      if (r) {
        fx.set(x.id, { style: "bloom", head: x.L, tail: 0, alpha: 0, comet: false, pulse: -1 });
        void fx.tween(x.id, "alpha", bloomAlpha(), REDUCED.bloom, EASE.linear, { signal: ac.signal }).then((ok) => ok && arriveSlot(x));
      } else {
        fx.set(x.id, { style: "bloom", head: 0, tail: 0, alpha: bloomAlpha(), comet: false, pulse: -1 });
        void fx.tween(x.id, "head", x.L, x.draw, EASE.drift, { from: 0, delay: x.start, signal: ac.signal, also: { comet: true } }).then((ok) => ok && arriveSlot(x));
      }
    }
    if (!plan.slots.length) finish();
    return d.promise;
  }

  function startBloomIdle(b: BloomRun): void {
    idleLoops++;
    void runIdleLoop({
      signal: b.ac.signal,
      first: FIRST_PACKET_AFTER_MS,
      interval: () => bloomPacketIntervalMs(rand()),
      blocked: () => !!sel || !!driving || !!intro || S().phase === "drive" || S().phase === "intro" || bloom !== b,
      reduced,
      tracker,
      packet: async (signal) => {
        const jobs = b.plan.slots.map(async (x) => {
          const tok = b.tokens.get(x.id);
          if (!tok || !fx.owns(x.id, tok)) return;
          fx.set(x.id, { pulseSigma: packetSigmaM(x.L) });
          await fx.tween(x.id, "pulse", x.L, packetMs(x.L / 1000), EASE.breathe, { from: 0, delay: x.index * b.plan.s, signal, idle: true });
          if (fx.owns(x.id, tok)) fx.set(x.id, { pulse: -1 });
        });
        await Promise.all(jobs);
      },
    }).finally(() => idleLoops--);
  }

  async function clearBloom(): Promise<void> {
    if (!bloom || destroyed) return;
    exitBloom(bloom, { following: false });
  }

  /* ── preview (§E.9) ──────────────────────────────────────────────── */

  function preview(id: string | null): void {
    if (destroyed || previewId === id) return;
    const prev = previewId;
    const prevTok = previewToken;
    previewId = id;
    previewToken = null;
    safe("setHovered", () => markers().setHovered(id));
    const r = reduced();
    if (prev && prevTok && fx.owns(prev, prevTok)) {
      void fx.tween(prev, "alpha", 0, r ? REDUCED.fade : 168, r ? EASE.linear : EASE.leave).then((ok) => {
        if (ok && fx.owns(prev, prevTok)) {
          fx.set(prev, { head: 0, tail: 0 });
          fx.release(prev, prevTok);
        }
      });
    }
    if (!id || kiosk() || intro || driving) return;
    const place = placeOf(id);
    if (!place || place.isSite || categoryOf(place)?.kind === "future") return;
    if (sel?.id === id || bloom?.tokens.has(id)) return; // already lit
    const rt = routeFor(place, S().routes);
    if (!rt) return;
    const tok = fx.claim(rt.id);
    previewToken = tok;
    fx.set(rt.id, { style: "preview", head: rt.lengthM, tail: 0, alpha: 0, comet: false, pulse: -1 });
    void fx.tween(rt.id, "alpha", PREVIEW_ALPHA, r ? REDUCED.fade : 240, r ? EASE.linear : EASE.settle);
  }

  /* ── the drive (§E.6) ────────────────────────────────────────────── */

  /** A selection in its arrived state (a drive started without select(), e.g. a deep link). */
  function adopt(id: string): Sel | null {
    const place = placeOf(id);
    const route = routeFor(place, S().routes);
    if (!place || !route) return null;
    const kind = categoryOf(place)?.kind ?? "poi";
    const { target, zoom } = frameFor(place, route, kind);
    const d = deferred();
    const s: Sel = {
      id,
      place,
      kind,
      route,
      L: route.lengthM,
      tl: selectTimeline(route.lengthM / 1000),
      t0: performance.now(),
      ac: new AbortController(),
      token: fx.claim(id),
      target,
      arriveZoom: zoom,
      arrived: true,
      lastP: 1,
      pt: -Infinity,
      corridor: null,
      resolve: d.resolve,
      done: d.promise,
    };
    d.resolve();
    if (sel) void leave(sel, "ab", s);
    sel = s;
    fx.set(id, { style: "hero", head: s.L, tail: 0, alpha: 1, comet: false, pulse: -1 });
    safe("markers", () => {
      markers().setSelected(id);
      markers().setDimmed(true);
    });
    if (siteLit) setSiteLight(false, 294, "leave");
    siteLit = false;
    return s;
  }

  function drive(id: string, o: { signal?: AbortSignal } = {}): Promise<"done" | "interrupted" | "cancelled"> {
    if (destroyed || o.signal?.aborted) return Promise.resolve("cancelled");
    if (driving) driving.stop("internal");
    settleAc?.abort();
    const place = placeOf(id);
    const route = routeFor(place, S().routes);
    if (!place || !route || reduced()) {
      emit("drive:end", { placeId: id, reason: "cancelled" });
      return Promise.resolve("cancelled");
    }
    if (intro) intro.finish();
    let s = sel && sel.id === id ? sel : adopt(id);
    if (!s) return Promise.resolve("cancelled");
    if (!s.arrived) {
      // Take the drive before the line has arrived: finish the draw now.
      s.ac.abort();
      const fresh = new AbortController();
      s.ac = fresh;
      arrive(s);
    }
    s = sel!;
    const selNow = s;
    const L = route.lengthM;
    const D = L / 1000;
    const T = driveMs(D);
    const zD = driveZoom(D);
    const ahead = lookAheadM(L);
    const geom: RouteGeom = { coords: route.coords as LngLat[], cum: route.cum, L };
    const t = displayKm(place);
    const spans = route.nameSpans;
    const ac = new AbortController();
    o.signal?.addEventListener("abort", () => run.stop("cancelled"), { once: true });
    const resumeP = lastInterrupt && lastInterrupt.id === id && performance.now() - lastInterrupt.at < RESUME_WINDOW_MS ? lastInterrupt.p : 0;
    lastInterrupt = null;
    const s0 = L * resumeP;
    const pad = restPadding();
    const b0 = resumeP > 0 ? chordBearing(geom, s0, ahead) : chordBearing(geom, 0, DRIVE_START_BEARING_M);
    let spring: Spring = { value: b0, velocity: 0 };
    const tok = selNow.token ?? fx.claim(id);
    selNow.token = tok;

    let resolveRun!: (r: "done" | "interrupted" | "cancelled") => void;
    const result = new Promise<"done" | "interrupted" | "cancelled">((res) => (resolveRun = res));
    let settled = false;
    let arrived = false; // the comet reached the pin; only the 2400 hold remains
    let offTick: (() => void) | null = null;
    const offs: Array<() => void> = [];
    let lastEmit = -Infinity;
    const emitProgress = (p: number, force = false) => {
      const now = performance.now();
      if (!force && now - lastEmit < PROGRESS_HZ_MS) return;
      lastEmit = now;
      emit("drive:progress", { placeId: id, p, kmLeft: t.km * (1 - p), minutesLeft: t.minutes * (1 - p), caption: captionAt(spans, L * p) });
    };
    const cleanup = () => {
      offTick?.();
      offTick = null;
      for (const off of offs.splice(0)) off();
      if (driving === run) driving = null;
    };
    const lightOff = () => {
      if (fx.owns(id, tok)) fx.set(id, { comet: false, pulse: -1, head: L, alpha: 1 });
    };
    const run: DriveRun = {
      id,
      ac,
      p: resumeP,
      stop: (reason) => {
        if (settled) return;
        settled = true;
        ac.abort();
        cleanup();
        if (arrived) {
          // stopped during the arrival hold: the ride is complete (drive:end "done" was sent at the pin)
          if (reason === "cancelled" && sel === selNow && selNow.target) void moveCamera(selNow.target, selNow.tl.Tc);
          resolveRun("done");
          return;
        }
        lightOff();
        if (reason === "interrupted") {
          lastInterrupt = { id, p: run.p, at: performance.now() };
          emit("drive:end", { placeId: id, reason: "interrupted" });
          resolveRun("interrupted");
          // the ride stopped where the visitor took over; the orientation comes back to them (§E.11)
          settleAfterTakeover(selNow);
          return;
        }
        emit("drive:end", { placeId: id, reason: "cancelled" });
        // "End the drive" / Esc: back to the select framing. An internal stop (clear, select, bloom) moves the camera itself.
        if (reason === "cancelled" && sel === selNow && selNow.target) void moveCamera(selNow.target, selNow.tl.Tc);
        resolveRun("cancelled");
      },
    };
    driving = run;

    // any input except Tab / Shift / Escape / Enter / Space ends the ride where it is
    const interrupt = () => run.stop("interrupted");
    offs.push(ctrl.onInteract(interrupt));
    offs.push(on("camera:interrupt", (e) => e.reason === "user" && interrupt()));
    if (typeof window !== "undefined") {
      const onKey = (e: KeyboardEvent) => {
        if (["Tab", "Shift", "Escape", "Enter", " ", "Control", "Alt", "Meta", "CapsLock"].includes(e.key)) return;
        const tg = e.target as HTMLElement | null;
        if (tg && (tg.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(tg.tagName))) return;
        interrupt();
      };
      window.addEventListener("keydown", onKey, true);
      offs.push(() => window.removeEventListener("keydown", onKey, true));
    }

    // enter: veil at 1, the route whole, the comet at s0, the camera to the ride's first frame
    setVeil(1, 420, "drift");
    fx.set(id, { style: "hero", head: L, tail: 0, alpha: 1, comet: true, pulse: s0, pulseSigma: DRIVE_PACKET_SIGMA_M });
    emitProgress(resumeP, true);
    const startCam: CameraTarget = { center: lookAt(geom, s0, ahead), zoom: zD, bearing: wrap360(b0), pitch: DRIVE_PITCH, padding: pad };
    lastTarget = startCam;
    void moveCamera(startCam, DRIVE_ENTER_MS, ac.signal).then((res) => {
      if (settled) return;
      if (res === "interrupted") return run.stop("interrupted");
      if (res === "cancelled" && ac.signal.aborted) return;
      // ride
      const tStart = performance.now() - driveTimeAt(resumeP, T);
      let last = performance.now();
      offTick = ticker.add((now) => {
        if (settled) return false;
        const dt = now - last;
        last = now;
        const p = driveP(now - tStart, T);
        run.p = p;
        const sM = L * p;
        const travel = chordBearing(geom, sM, ahead);
        spring = stepBearing(spring, travel, dt, { travel });
        try {
          ctrl.jumpTo({ center: lookAt(geom, sM, ahead), zoom: zD, bearing: wrap360(spring.value), pitch: DRIVE_PITCH, padding: pad });
        } catch {
          /* map gone */
        }
        if (fx.owns(id, tok)) fx.set(id, { pulse: sM });
        if (p >= 1) {
          arriveDrive();
          return false;
        }
        emitProgress(p);
        return true;
      });
    });

    const arriveDrive = () => {
      if (settled) return;
      arrived = true;
      offTick = null;
      for (const off of offs.splice(0)) off();
      if (fx.owns(id, tok)) fx.set(id, { comet: false, pulse: -1 });
      emitProgress(1, true);
      emit("drive:end", { placeId: id, reason: "done" });
      const at = place.coord as LngLat;
      safe("pulse", () => markers().pulse(at, { count: RINGS.count, staggerMs: RINGS.staggerMs, radiusPx: ringRadiusPx(kiosk()), durationMs: RINGS.durationMs }));
      safe("lightContours", () => overlays().lightContours(at, { zoom: zD, radiusPx: litRadiusPx(kiosk()), rest: LIT.rest, durationMs: LIT.riseMs }));
      // hold "Arrived at …" (2400), then the camera eases back to the select framing and the chrome returns
      void sleep(DRIVE_ARRIVE_HOLD_MS, ac.signal).then((ok) => {
        if (settled) return;
        settled = true;
        cleanup();
        if (ok && sel === selNow && selNow.target) void moveCamera(selNow.target, selNow.tl.Tc);
        resolveRun(ok ? "done" : "cancelled");
      });
    };

    return result;
  }

  /**
   * After an interrupted drive (§E.6 Stop, §E.11): the camera stays where the visitor took over (their
   * centre and zoom), and once their gesture has ended the bearing and pitch ease back to the §D.11
   * select pose (bearing within ±40°, the select pitch) over 640 settle. Rotation and user pitch are off,
   * so without this a stopped ride would leave the map pitched 60° and possibly south-up. If the visitor
   * takes over again during the ease, it waits for that gesture too. Resume re-enters the drive from here.
   */
  let settleAc: AbortController | null = null;
  const TAKEOVER_QUIET_MS = 250;
  async function gestureEnded(signal: AbortSignal): Promise<boolean> {
    for (let i = 0; i < 120; i++) {
      if (!(await sleep(TAKEOVER_QUIET_MS / 2, signal))) return false;
      let moving: boolean;
      try {
        moving = ctrl.isMoving();
      } catch {
        return false;
      }
      const sincePointer = performance.now() - tracker.lastPointerAt;
      if (!moving && tracker.pointersDown === 0 && sincePointer >= TAKEOVER_QUIET_MS) return true;
    }
    return false;
  }
  function settleAfterTakeover(s: Sel): void {
    settleAc?.abort();
    const ac = (settleAc = new AbortController());
    const bearing = s.target?.bearing ?? 0;
    const pitch = s.target?.pitch ?? 0;
    void (async () => {
      for (let tries = 0; tries < 6; tries++) {
        if (!(await gestureEnded(ac.signal))) return;
        if (destroyed || ac.signal.aborted || sel !== s || driving || intro) return;
        let m: ReturnType<MapController["map"]>;
        try {
          m = ctrl.map();
        } catch {
          return;
        }
        const b = signedBearing(m.getBearing());
        const p = m.getPitch();
        if (Math.abs(b - bearing) < 0.5 && Math.abs(p - pitch) < 0.5) return;
        const c = m.getCenter();
        const pad = typeof m.getPadding === "function" ? m.getPadding() : undefined;
        const r = reduced();
        const res = await ctrl
          .easeTo(
            { center: [c.lng, c.lat], zoom: m.getZoom(), bearing, pitch, ...(pad ? { padding: { top: pad.top, right: pad.right, bottom: pad.bottom, left: pad.left } } : {}) },
            { duration: r ? REDUCED.fade : TAKEOVER_SETTLE_MS, ease: r ? "linear" : "settle", signal: ac.signal },
          )
          .catch(() => "cancelled" as MoveResult);
        if (res !== "interrupted") return;
      }
    })().finally(() => {
      if (settleAc === ac) settleAc = null;
    });
  }

  /* ── home (§D.4, §D.10, kiosk scene 0) ───────────────────────────── */

  async function home(): Promise<void> {
    if (destroyed) return;
    if (intro) intro.finish();
    if (driving) driving.stop("internal");
    const r = reduced();
    const quiet = !sel && !bloom;
    if (sel) {
      const s = sel;
      sel = null;
      void leave(s, "clear").then(() => restoreSite(0));
      safe("lightContours", () => overlays().lightContours(null));
      safe("markers", () => {
        markers().setSelected(null);
        markers().setDimmed(false);
      });
    }
    if (bloom) {
      exitBloom(bloom, { following: true });
      safe("dim", () => markers().setDimmed(false));
    }
    setVeil(0, EXIT * 1100, "leave");
    const target = homeCamera();
    let kind: "ease" | "fly" = "ease";
    try {
      const { w, h } = viewSize();
      kind = chooseMove(currentCam(), target, w, h);
    } catch {
      /* ease */
    }
    const dur = kind === "ease" ? 1100 : 1800;
    if (quiet) restoreSite(0);
    const res = await moveCamera(target, dur);
    if (destroyed || sel || bloom || res === "cancelled") return;
    // the land answers at the site: tree rings, the site's contours light and fade, a packet around the outline
    const c = siteCenter();
    if (!r) {
      safe("pulse", () => markers().pulse(c, { count: RINGS.count, staggerMs: RINGS.staggerMs, radiusPx: ringRadiusPx(kiosk()), durationMs: RINGS.durationMs }));
      safe("lightContours", () => overlays().lightContours(c, { zoom: target.zoom, radiusPx: litRadiusPx(kiosk()), rest: 0, durationMs: LIT.siteRiseMs, ease: "drift" }));
      if (site && siteLit) {
        fx.set(SITE_PATH_ID, { pulseSigma: packetSigmaM(siteL) });
        void fx.tween(SITE_PATH_ID, "pulse", siteL, packetMs(siteL / 1000), EASE.breathe, { from: 0 }).then((ok) => ok && fx.set(SITE_PATH_ID, { pulse: -1 }));
      }
    }
  }

  /* ── intro (§E.7) ────────────────────────────────────────────────── */

  function introRun(o: { mode?: "full" | "short"; signal?: AbortSignal } = {}): Promise<void> {
    if (destroyed) return Promise.resolve();
    if (intro) return intro.done;
    const r = reduced();
    const full = o.mode !== "short" && !r;
    const ac = new AbortController();
    const d = deferred();
    const target: Mood = S().mood;
    const beats = new Set<IntroBeat>();
    const beat = (b: IntroBeat) => {
      if (beats.has(b)) return;
      beats.add(b);
      emit("intro:beat", { beat: b });
    };
    const layers = () => S().layers;
    const fadeContours = (v: number, ms: number) => {
      const f = handle.fadeContours;
      if (f) void f.call(handle, v, { duration: ms, ease: "drift" });
    };
    let finished = false;

    /** Everything at its final state; the overlay (B6) cross-fades over 420. */
    const finalState = (ms: number) => {
      if (finished) return;
      finished = true;
      ac.abort();
      if (intro === run) intro = null;
      try {
        ctrl.jumpTo(homeCamera());
      } catch {
        /* map gone */
      }
      if (ctrl.getMood() !== target) ctrl.setMood(target, { duration: ms });
      actions.setMood(target);
      fadeContours(1, ms);
      safe("site", () => markers().setSite({ visible: true, progress: 1, label: true }));
      safe("boundary", () => overlays().setBoundary({ visible: layers().boundary, fill: 1 }));
      safe("rings", () => void overlays().setRings({ visible: layers().rings }));
      safe("future", () => overlays().setFuture({ visible: layers().future, highlight: null }));
      if (site) {
        fx.set(SITE_PATH_ID, { style: "site", head: siteL, tail: 0, comet: false, pulse: -1 });
        if (siteWanted()) {
          siteLit = true;
          void fx.tween(SITE_PATH_ID, "alpha", 1, ms, EASE.drift);
        }
      }
      beat("ui");
      void sleep(ms).then(() => {
        if (!destroyed) beat("done");
        d.resolve();
      });
    };

    const run: IntroRun = { ac, done: d.promise, finish: () => finalState(INTRO.shortMs) };
    intro = run;
    const skip = o.signal?.aborted ?? false;
    o.signal?.addEventListener("abort", () => finalState(INTRO.shortMs), { once: true });

    // start: region view at night, contours hidden, the site dark
    siteAc?.abort();
    siteLit = false;
    if (site) fx.set(SITE_PATH_ID, { style: "site", head: 0, tail: 0, alpha: 0, comet: false, pulse: -1 });
    safe("veil", () => overlays().setVeil(0, { duration: 0 }));
    safe("lightContours", () => overlays().lightContours(null));
    beat("start");
    if (!full || skip) {
      finalState(INTRO.shortMs);
      return d.promise;
    }
    ctrl.setMood("night", { duration: 0 });
    try {
      ctrl.jumpTo(ctrl.regionTarget());
    } catch {
      /* map gone */
    }
    fadeContours(0, 0);
    safe("site", () => markers().setSite({ visible: false, progress: 0, label: false }));
    safe("boundary", () => overlays().setBoundary({ visible: false, fill: 0 }));
    safe("rings", () => void overlays().setRings({ visible: false }));
    safe("future", () => overlays().setFuture({ visible: false, highlight: null })); // the opening shows the land alone

    const t0 = performance.now();
    const at = (ms: number) => sleep(t0 + ms - performance.now(), ac.signal);
    void (async () => {
      if (!(await at(INTRO_BEATS.line1))) return;
      beat("line1");
      if (!(await at(INTRO_BEATS.contours))) return;
      beat("contours");
      fadeContours(1, INTRO.contoursMs);
      if (!(await at(INTRO_BEATS.line2))) return;
      beat("line2");
      if (!(await at(INTRO_BEATS.sunrise))) return;
      beat("sunrise");
      ctrl.setMood(target, { duration: MOOD_SUNRISE_MS });
      actions.setMood(target);
      if (!(await at(INTRO_BEATS.fly))) return;
      beat("fly");
      const home = homeCamera();
      void ctrl.flyTo(home, { duration: INTRO.flyMs, curve: INTRO.curve, ease: "drift", signal: ac.signal });
      if (!(await at(INTRO_BEATS.site))) return;
      beat("site");
      // the outline draws itself while the Whorl fills; the contours around the site rise with it, then fade
      if (site) {
        fx.set(SITE_PATH_ID, { style: "site", head: 0, tail: 0, alpha: 1, comet: false, pulse: -1 });
        void fx
          .tween(SITE_PATH_ID, "head", siteL, INTRO.siteMs, EASE.drift, { from: 0, signal: ac.signal, also: { comet: true } })
          .then((ok) => ok && fx.set(SITE_PATH_ID, { comet: false }));
        siteLit = true;
      }
      safe("site", () => markers().setSite({ visible: true, progress: 0, label: true }));
      void tween(INTRO.siteMs, EASE.drift, (e) => safe("site", () => markers().setSite({ progress: e })), { signal: ac.signal });
      safe("boundary", () => overlays().setBoundary({ visible: layers().boundary, fill: 1 }));
      const c = siteCenter();
      safe("lightContours", () => overlays().lightContours(c, { zoom: home.zoom, radiusPx: litRadiusPx(kiosk()), rest: 0, durationMs: LIT.siteRiseMs, ease: "drift" }));
      if (!(await at(INTRO_BEATS.rings))) return;
      beat("rings");
      safe("pulse", () => markers().pulse(c, { count: RINGS.count, staggerMs: RINGS.staggerMs, radiusPx: ringRadiusPx(kiosk()), durationMs: RINGS.durationMs }));
      safe("rings", () => void overlays().setRings({ visible: layers().rings, animate: true }));
      if (!(await at(INTRO_BEATS.ui))) return;
      beat("ui");
      safe("future", () => overlays().setFuture({ visible: layers().future, highlight: null }));
      if (!(await at(INTRO_BEATS.done))) return;
      finished = true;
      if (intro === run) intro = null;
      beat("done");
      d.resolve();
    })();
    return d.promise;
  }

  /* ── cancel / destroy ────────────────────────────────────────────── */

  /** Fast-forward: whatever is on screen settles into its resting state; the camera stops where it is. */
  function cancel(): void {
    if (destroyed) return;
    if (intro) intro.finish();
    if (driving) driving.stop("internal");
    try {
      ctrl.stop();
    } catch {
      /* map gone */
    }
    for (const f of [...exits]) f();
    const s = sel;
    if (s && !s.arrived) {
      s.ac.abort();
      s.ac = new AbortController();
      if (s.route) fx.set(s.id, { head: s.L, tail: 0, alpha: 1, comet: false });
      arrive(s);
    }
    const b = bloom;
    if (b && !b.finished) {
      b.ac.abort();
      b.ac = new AbortController();
      for (const x of b.plan.slots) {
        const tok = b.tokens.get(x.id);
        if (tok && fx.owns(x.id, tok)) fx.set(x.id, { head: x.L, tail: 0, alpha: sel ? CONTEXT_ALPHA : 1, comet: false });
        if (!b.arrived.has(x.id)) {
          b.arrived.add(x.id);
          emit("bloom:arrive", { categoryId: b.cat, placeId: x.id, index: x.index });
        }
      }
      if (b.kind !== "future") safe("focus", () => markers().setFocusSet([...b.arrived]));
      if (b.kind === "road") safe("setRoads", () => overlays().setRoads({ visible: true, highlight: null, bloom: b.corridors }));
      b.finished = true;
      emit("bloom:done", { categoryId: b.cat });
      b.resolve();
      if (b.kind !== "future" && !reduced()) startBloomIdle(b);
    }
    if (previewId) preview(null);
    emit("camera:interrupt", { reason: "cancel" });
  }

  function destroy(): void {
    if (destroyed) return;
    settleAc?.abort();
    intro?.ac.abort();
    driving?.stop("internal");
    sel?.ac.abort();
    sel?.resolve();
    bloom?.ac.abort();
    bloom?.resolve();
    siteAc?.abort();
    destroyed = true;
    fx.stopAll();
    tracker.stop();
    exits.clear();
    sel = null;
    bloom = null;
    intro = null;
    driving = null;
  }

  return {
    select,
    clear,
    bloom: bloomStart,
    clearBloom,
    preview,
    drive,
    intro: introRun,
    home,
    cancel,
    destroy,
    framing(id: string): CameraTarget | null {
      const p = placeOf(id);
      if (!p) return null;
      const kind = categoryOf(p)?.kind ?? "poi";
      return frameFor(p, kind === "future" ? null : routeFor(p, S().routes), kind).target;
    },
    debug(): ChoreoDebug {
      return {
        selected: sel?.id ?? null,
        arrived: !!sel?.arrived,
        bloom: bloom?.cat ?? null,
        bloomArrived: bloom?.arrived.size ?? 0,
        driving: driving?.id ?? null,
        intro: !!intro,
        preview: previewId,
        siteLit,
        lastTarget,
        lastTiming,
        idleLoops,
      };
    },
  };
}
